interface TimeCodeData {
  sessionId: string;
  startTime: string;
  endTime?: string;
  name?: string;
  snippetName: string;
}

/**
 * Parse time code markup from markdown links
 * Format: [snippet_name](timecode://session_id/start_time/end_time?name=description)
 * Format: [snippet_name](timecode://session_id/single_time?name=description)
 */
export const parseTimeCode = (href: string, linkText: string): TimeCodeData | null => {
  try {
    // console.log('[parseTimeCode] Called with href:', href, 'linkText:', linkText);
    
    if (!href.startsWith('timecode://')) {
      // console.log('[parseTimeCode] Not a timecode link');
      return null;
    }

    // Remove timecode:// prefix
    const cleanHref = href.replace('timecode://', '');
    
    // Split on ? to separate path from query params
    const [pathPart, queryPart] = cleanHref.split('?');
    
    // Parse path: session_id/start_time or session_id/start_time/end_time
    const pathSegments = pathPart.split('/');
    
    if (pathSegments.length < 2) {
      console.warn('Invalid timecode format: missing session_id or time');
      return null;
    }

    const sessionId = pathSegments[0];
    const startTime = pathSegments[1];
    const endTime = pathSegments.length > 2 ? pathSegments[2] : undefined;

    // Validate time format (HH:MM:SS)
    const timeRegex = /^\d{2}:\d{2}:\d{2}$/;
    if (!timeRegex.test(startTime)) {
      console.warn('Invalid start time format:', startTime);
      return null;
    }
    
    if (endTime && !timeRegex.test(endTime)) {
      console.warn('Invalid end time format:', endTime);
      return null;
    }

    // Parse query parameters
    let name: string | undefined;
    if (queryPart) {
      const queryParams = new URLSearchParams(queryPart);
      name = queryParams.get('name') || undefined;
    }

    return {
      sessionId,
      startTime,
      endTime,
      name,
      snippetName: linkText
    };
  } catch (error) {
    console.error('Error parsing timecode:', error);
    return null;
  }
};

/**
 * Generate time code markup for LLM responses
 */
export const generateTimeCodeMarkup = (
  snippetName: string,
  sessionId: string,
  startTime: string,
  endTime?: string,
  description?: string
): string => {
  let url = `timecode://${sessionId}/${startTime}`;
  
  if (endTime) {
    url += `/${endTime}`;
  }
  
  if (description) {
    url += `?name=${encodeURIComponent(description)}`;
  }
  
  return `[${snippetName}](${url})`;
};

/**
 * Convert seconds to HH:MM:SS format
 */
export const secondsToTimeCode = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

/**
 * Convert HH:MM:SS format to seconds
 */
export const timeCodeToSeconds = (timeCode: string): number => {
  const [hours, minutes, seconds] = timeCode.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

/**
 * Validate that time codes are in correct format and order
 */
export const validateTimeCode = (startTime: string, endTime?: string): boolean => {
  const timeRegex = /^\d{2}:\d{2}:\d{2}$/;
  
  if (!timeRegex.test(startTime)) {
    return false;
  }
  
  if (endTime) {
    if (!timeRegex.test(endTime)) {
      return false;
    }
    
    // Check that end time is after start time
    const startSeconds = timeCodeToSeconds(startTime);
    const endSeconds = timeCodeToSeconds(endTime);
    
    if (endSeconds <= startSeconds) {
      return false;
    }
  }
  
  return true;
};

/**
 * Extract all time codes from a markdown text
 */
export const extractTimeCodesFromText = (text: string): TimeCodeData[] => {
  const timeCodes: TimeCodeData[] = [];
  
  // Regex to match markdown links with timecode protocol
  const linkRegex = /\[([^\]]+)\]\(timecode:\/\/[^)]+\)/g;
  
  let match;
  while ((match = linkRegex.exec(text)) !== null) {
    const linkText = match[1];
    const href = match[0].match(/\(([^)]+)\)/)?.[1];
    
    if (href) {
      const timeCodeData = parseTimeCode(href, linkText);
      if (timeCodeData) {
        timeCodes.push(timeCodeData);
      }
    }
  }
  
  return timeCodes;
};